import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Form, Input, Modal, Upload, message } from 'antd';
import { InboxOutlined, FilePdfOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { NAMESPACE } from '@/constants';
import { useUploadPrismaFileMutation, useLazyGetPrismaFileLinkQuery } from '@/api';

interface Props {
  /** Plan the Prisma order belongs to, or null when the dialog is closed. */
  mediaPlanId: string | null;
  /** Document title, shown so the user can see which IO they are confirming. */
  documentName?: string;
  /** A file already uploaded for this plan, if any. */
  existingFileName?: string;
  onClose: () => void;
}

/**
 * Confirms a Prisma order and attaches its PDF.
 *
 * Uploading is the confirmation: a campaign booked through Prisma has its line
 * items withheld from the booking sheet until the order comes back, and having
 * the signed document in hand is what coming back means. Splitting the two
 * would leave bookings held up by a second step nobody realised was pending.
 */
export const PrismaModal: FC<Props> = ({
  mediaPlanId,
  documentName,
  existingFileName,
  onClose
}) => {
  const { t } = useTranslation(NAMESPACE.MEDIA_PLAN_PAGE);
  const [form] = Form.useForm<{ prismaReference?: string }>();
  const [file, setFile] = useState<UploadFile | null>(null);
  const [uploadPrismaFile, { isLoading }] = useUploadPrismaFileMutation();
  const [fetchLink] = useLazyGetPrismaFileLinkQuery();

  const close = () => {
    form.resetFields();
    setFile(null);
    onClose();
  };

  const handleConfirm = async () => {
    if (!mediaPlanId) return;

    const raw = file?.originFileObj as File | undefined;
    if (!raw) {
      message.warning(t('prismaFileRequired'));
      return;
    }

    try {
      const values = await form.validateFields();
      const result = await uploadPrismaFile({
        mediaPlanId,
        file: raw,
        prismaReference: values.prismaReference?.trim() || undefined
      }).unwrap();

      /* Says whether the booking actually went through, rather than only that
         the file saved: an IO that is not yet signed has nothing to release,
         and the user should not be left assuming it reached the sheet. */
      message.success(result.released ? t('prismaReleased') : t('prismaSavedNotReleased'));
      close();
    } catch (err) {
      const status = (err as { status?: number })?.status;
      if (status === 503) {
        /* The backend explains what is wrong and how to fix it — a bucket that
           was never created, say — so its message is shown rather than
           replaced with generic copy that would leave an admin guessing. */
        const detail = (err as { data?: { message?: string } })?.data?.message;
        message.error(detail || t('prismaStorageUnavailable'), 8);
        return;
      }
      if (status === 400 || status === 404) {
        message.error((err as { data?: { message?: string } })?.data?.message ?? t('prismaFailed'));
        return;
      }
      message.error(t('prismaFailed'));
    }
  };

  /** Opens the stored document through a freshly signed link. */
  const viewExisting = async () => {
    if (!mediaPlanId) return;
    try {
      const { url } = await fetchLink(mediaPlanId).unwrap();
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      message.error(t('prismaLinkFailed'));
    }
  };

  return (
    <Modal
      open={Boolean(mediaPlanId)}
      title={t('prismaModalTitle')}
      onCancel={close}
      okText={t('prismaConfirmAction')}
      onOk={handleConfirm}
      confirmLoading={isLoading}
      destroyOnClose
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={t('prismaModalHint')}
        description={documentName}
      />

      {existingFileName && (
        <Alert
          type="success"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('prismaAlreadyUploaded', { fileName: existingFileName })}
          action={
            <Button type="link" size="small" icon={<FilePdfOutlined />} onClick={viewExisting}>
              {t('prismaViewFile')}
            </Button>
          }
        />
      )}

      <Form form={form} layout="vertical">
        <Form.Item label={t('prismaFileLabel')} required>
          {/* Held until the user confirms rather than uploaded on drop, so
              choosing a file is not itself the act that releases a booking. */}
          <Upload.Dragger
            accept="application/pdf,.pdf"
            maxCount={1}
            beforeUpload={() => false}
            fileList={file ? [file] : []}
            onChange={({ fileList }) => setFile(fileList[0] ?? null)}
            onRemove={() => setFile(null)}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">{t('prismaUploadText')}</p>
            <p className="ant-upload-hint">{t('prismaUploadHint')}</p>
          </Upload.Dragger>
        </Form.Item>

        <Form.Item name="prismaReference" label={t('prismaReferenceLabel')}>
          <Input placeholder={t('prismaReferencePlaceholder')} />
        </Form.Item>
      </Form>
    </Modal>
  );
};
